/**
 * Per-version payload parsers for SERPRO/VIO QR codes.
 *
 * The outer {@link Framing} (see `framing.ts`) yields a `version` and a
 * `payload`; this module turns that payload into a {@link ParsedContainer}. Each
 * version has its own byte layout, text encoder, and — crucially — its own
 * reconstruction of the exact bytes the signature covers (`signedData`).
 *
 * Layouts were reverse-engineered from the VIO app `br.gov.serpro.lince`
 * (parsers `m8.c`=v1, `n2.c`=v2, `p4.d`=v3, `p4.f`=v4, `p4.h`=v5). See the design
 * spec in `docs/superpowers/specs/`.
 */
import type { Framing } from "./framing";
import { decode6bit } from "./cnh";
import { decodeBase91, decode7bitAccented } from "./decoders";
import { concatBytes, latin1Decode, latin1Encode, u16be } from "./bytes";

/** The fully parsed contents of a document QR, independent of version. */
export interface ParsedContainer {
  /** Template identifier (from the payload's field0) — selects the field schema. */
  templateId: number;
  /** QR format version. */
  version: number;
  /** Document creation time (unix seconds) when the hex framing was used, else `null`. */
  timestamp: number | null;
  /** Exact byte sequence the signature was computed over (per-version reconstruction). */
  signedData: Uint8Array;
  /** The document signature (ECDSA DER, or a fixed 256-byte RSA signature). */
  signature: Uint8Array;
  /** Text fields, split on the version's separator. */
  fields: string[];
  /** The raw decoded field string before splitting. */
  fieldsText: string;
  /** Embedded face image bytes (BPG with its magic prefix stripped). */
  imageData: Uint8Array;
  /** Length of {@link imageData}. */
  imageDataLength: number;
  /** Trailing bytes after the documented fields, if any. */
  extra: Uint8Array | null;
  /** field0 — the 2-byte template selector at the start of the payload. */
  field0: Uint8Array;
  /** The 2-byte image-length prefix, or an empty array for layouts without one (v2). */
  imgLenPrefix: Uint8Array;
  /** The 2-byte text-length prefix, or an empty array for layouts without one. */
  txtLenPrefix: Uint8Array;
  /** The raw (still-encoded) text bytes. */
  textData: Uint8Array;
  /**
   * Whether this version's byte layout is confirmed against a real document.
   * `false` for versions implemented from decompilation without a reference
   * sample (v1, v3, v4) — treat their output with caution.
   */
  verified: boolean;
}

/**
 * Text decoders differ per version: 6-bit for v2/v5, basE91 for v1/v3, and a
 * 7-bit accented alphabet for v4.
 */
type TextDecoder = (bytes: Uint8Array) => string;

const decode6 = (b: Uint8Array) => decode6bit(b).trim();
const decodeB91 = (b: Uint8Array) => latin1Decode(decodeBase91(b)).trim();
const decode7 = (b: Uint8Array) => decode7bitAccented(b).trim();

/** Versions implemented from decompilation but not verified against a real sample. */
export const UNVERIFIED_VERSIONS = new Set([1, 3, 4]);

/** A parser turns a framing (payload + header values) into a {@link ParsedContainer}. */
type VersionParser = (framing: Framing) => ParsedContainer;

/** Copies a slice of {@link a} — the container returns independent arrays, not views. */
const slice = (a: Uint8Array, start?: number, end?: number) => a.slice(start, end);

/** The numeric header value: timestamp for hex framing, id for binary framing. */
function headerValue(f: Framing): number {
  return f.timestamp ?? f.id ?? 0;
}

/**
 * The ASCII-hex signed-data prefix used by the hex-framing parsers (v2, v3):
 * `hex(value, 8 chars) + hex(version, 2 chars)`, matching the app's
 * `Long.toHexString`/`Integer.toHexString` zero-padded reconstruction.
 */
function hexPrefix(f: Framing): Uint8Array {
  const value = headerValue(f) >>> 0;
  const ts = value.toString(16).padStart(8, "0");
  const ver = f.version.toString(16).padStart(2, "0");
  return latin1Encode(ts + ver);
}

/**
 * The raw binary signed-data prefix used by the length-prefixed parsers (v4, v5):
 * `uint32BE(value) + versionByte`. This is the classic CNH-e header form.
 */
function binaryPrefix(f: Framing): Uint8Array {
  const value = headerValue(f) >>> 0;
  return new Uint8Array([
    (value >>> 24) & 0xff,
    (value >>> 16) & 0xff,
    (value >>> 8) & 0xff,
    value & 0xff,
    f.version & 0xff,
  ]);
}

/**
 * Version 2 (`n2.c`, "QRCode2"). Layout:
 * `templateId(2) | txtLen(2) | text | signature(256) | image(rest)`.
 * Text is 6-bit encoded (`m6.x0.a`); fields are `^`-separated. Signed data is
 * `hexPrefix + payload[0 .. 4+txtLen] + image` (everything but the signature).
 */
const parseV2: VersionParser = (f) => {
  const p = f.payload;
  const templateId = u16be(p, 0);
  const txtLen = u16be(p, 2);
  const textStart = 4;
  const text = p.subarray(textStart, textStart + txtLen);
  const sigStart = textStart + txtLen;
  const signature = p.subarray(sigStart, sigStart + 256);
  const imageData = p.subarray(sigStart + 256);

  const fieldsText = decode6(text);
  const signedData = concatBytes(hexPrefix(f), p.subarray(0, sigStart), imageData);

  return {
    templateId,
    version: f.version,
    timestamp: f.timestamp,
    signedData,
    signature: slice(signature),
    fields: fieldsText.split("^"),
    fieldsText,
    imageData: slice(imageData),
    imageDataLength: imageData.length,
    extra: null,
    field0: slice(p, 0, 2),
    imgLenPrefix: new Uint8Array(0), // v2 has no image-length prefix; image is the remainder
    txtLenPrefix: slice(p, 2, 4),
    textData: slice(text),
    verified: true,
  };
};

/**
 * Version 3 (`p4.d`). Layout:
 * `field0(2) | signature(256) | imgLen(2) | image | txtLen(2) | text | extra`.
 * Text is basE91-encoded; fields are `^`-separated. Signed data is
 * `hexPrefix + field0 + imgLenPrefix + image + txtLenPrefix + text`
 * (the fixed 256-byte signature is excluded). UNVERIFIED — no reference sample.
 */
const parseV3: VersionParser = (f) => {
  const p = f.payload;
  const templateId = u16be(p, 0);
  const signature = p.subarray(2, 258);

  const imgLen = u16be(p, 258);
  const imgStart = 260;
  const imageData = p.subarray(imgStart, imgStart + imgLen);

  const txtLenPos = imgStart + imgLen;
  const txtLen = u16be(p, txtLenPos);
  const txtStart = txtLenPos + 2;
  const text = p.subarray(txtStart, txtStart + txtLen);

  const extraStart = txtStart + txtLen;
  const extra = p.length > extraStart ? slice(p, extraStart) : null;

  const fieldsText = decodeB91(text);
  const signedData = concatBytes(
    hexPrefix(f),
    p.subarray(0, 2), // field0
    p.subarray(258, 258 + 2 + imgLen), // imgLen prefix + image
    p.subarray(txtLenPos, txtLenPos + 2 + txtLen), // txtLen prefix + text
  );

  return {
    templateId,
    version: f.version,
    timestamp: f.timestamp,
    signedData,
    signature: slice(signature),
    fields: fieldsText.split("^"),
    fieldsText,
    imageData: slice(imageData),
    imageDataLength: imageData.length,
    extra,
    field0: slice(p, 0, 2),
    imgLenPrefix: slice(p, 258, 260),
    txtLenPrefix: slice(p, txtLenPos, txtLenPos + 2),
    textData: slice(text),
    verified: false,
  };
};

/**
 * Version 1 (`m8.c`). A fully text-delimited layout: the payload is a Latin-1
 * string `data\signature`; `data` is `templateIdHex-fields-image`; every part is
 * basE91-encoded and fields are `¬`-separated. Signed data is
 * `hexPrefix + templateIdHex + "-" + fields + "-" + image`.
 * UNVERIFIED — no reference sample.
 */
const parseV1: VersionParser = (f) => {
  const s = latin1Decode(f.payload);
  const backslash = s.indexOf("\\");
  if (backslash < 0) throw new Error("v1 QR: could not split data from signature");
  const dataStr = s.slice(0, backslash);
  const sigStr = s.slice(backslash + 1);
  const signature = decodeBase91(latin1Encode(sigStr));

  const parts = dataStr.split("-");
  if (parts.length !== 3) throw new Error("v1 QR: expected templateId-fields-image");
  const [tmplHex, fieldsEnc, imageEnc] = parts as [string, string, string];
  const templateId = parseInt(tmplHex, 16);

  const fieldsText = latin1Decode(decodeBase91(latin1Encode(fieldsEnc))).trim();
  const imageData = imageEnc ? decodeBase91(latin1Encode(imageEnc)) : new Uint8Array(0);

  const signedData = concatBytes(
    hexPrefix(f),
    latin1Encode(`${tmplHex}-${fieldsText}-`),
    imageData,
  );

  return {
    templateId,
    version: f.version,
    timestamp: f.timestamp,
    signedData,
    signature,
    fields: fieldsText.split("¬"),
    fieldsText,
    imageData,
    imageDataLength: imageData.length,
    extra: null,
    field0: new Uint8Array(0),
    imgLenPrefix: new Uint8Array(0),
    txtLenPrefix: new Uint8Array(0),
    textData: latin1Encode(fieldsEnc),
    verified: false,
  };
};

/**
 * Versions 4 (`p4.f`) and 5 (`p4.h`) share one length-prefixed layout:
 * `field0(2) | sigLen(2) | sig | imgLen(2) | img | txtLen(2) | text | extra`.
 * Text is 6-bit (v5) or 7-bit accented (v4); fields are `^`-separated. Signed
 * data is `binaryPrefix + field0 + imgLenPrefix + image + txtLenPrefix + text`
 * (the length-prefixed signature block is excluded) — the form reader-cnh has
 * always used for v5 and which verifies against SERPRO certificates.
 */
function makeLengthPrefixed(textDecoder: TextDecoder, verified: boolean): VersionParser {
  return (f) => {
    const p = f.payload;
    const templateId = u16be(p, 0);
    const sigLen = u16be(p, 2);
    const sigStart = 4;
    const signature = p.subarray(sigStart, sigStart + sigLen);

    const imgLenPos = sigStart + sigLen;
    const imgLen = u16be(p, imgLenPos);
    const imgStart = imgLenPos + 2;
    const imageData = p.subarray(imgStart, imgStart + imgLen);

    const txtLenPos = imgStart + imgLen;
    const txtLen = u16be(p, txtLenPos);
    const txtStart = txtLenPos + 2;
    const text = p.subarray(txtStart, txtStart + txtLen);

    const extraStart = txtStart + txtLen;
    const extra = p.length > extraStart ? slice(p, extraStart) : null;

    const fieldsText = textDecoder(text);
    const signedData = concatBytes(
      binaryPrefix(f),
      p.subarray(0, 2), // field0
      p.subarray(imgLenPos, imgLenPos + 2 + imgLen), // imgLen prefix + image
      p.subarray(txtLenPos, txtLenPos + 2 + txtLen), // txtLen prefix + text
    );

    return {
      templateId,
      version: f.version,
      timestamp: f.timestamp,
      signedData,
      signature: slice(signature),
      fields: fieldsText.split("^"),
      fieldsText,
      imageData: slice(imageData),
      imageDataLength: imageData.length,
      extra,
      field0: slice(p, 0, 2),
      imgLenPrefix: slice(p, imgLenPos, imgLenPos + 2),
      txtLenPrefix: slice(p, txtLenPos, txtLenPos + 2),
      textData: slice(text),
      verified,
    };
  };
}

/** Version 5 (`p4.h`): length-prefixed, 6-bit text. Verified against real samples. */
const parseV5 = makeLengthPrefixed(decode6, true);

/**
 * Version 4 (`p4.f`): same length-prefixed layout as v5, but the text uses the
 * 7-bit accented alphabet (`c5.b.a`). UNVERIFIED — no reference sample.
 */
const parseV4 = makeLengthPrefixed(decode7, false);

/**
 * Registry of implemented version parsers. v2 and v5 are verified against real
 * documents; v1, v3 and v4 are implemented from decompilation but unverified
 * (see {@link UNVERIFIED_VERSIONS}). Version 6 is undecoded and unsupported.
 */
const PARSERS: Record<number, VersionParser> = {
  1: parseV1,
  2: parseV2,
  3: parseV3,
  4: parseV4,
  5: parseV5,
};

/**
 * Parses the payload of a framed QR into a {@link ParsedContainer}, dispatching
 * by version.
 *
 * @throws {Error} If the version has no implemented parser.
 */
export function parseContainer(framing: Framing): ParsedContainer {
  const parser = PARSERS[framing.version];
  if (!parser) {
    throw new Error(`Unsupported QR version: ${framing.version}`);
  }
  return parser(framing);
}
